
import { Component, Vue, Watch } from 'vue-property-decorator'
import { GetRanking, GetRepairSizeRanking, GetCheckSizeRanking, GetRepairMaterialRanking } from '@/api/leaks'
import { ListDepts, ListProjects } from '@/api/orgAdmin'
import { ListOrgUsers } from '@/api/orgAdmin'
import { IAllDeptInfo, IAllUserInfo, IProject, LeakStatus } from '@/api/types'
import { IsMobile } from '@/utils/check'
// 导入 echarts
// @ts-ignore
import * as echarts from 'echarts/lib/echarts'
// @ts-ignore
import 'echarts/lib/chart/bar'
// @ts-ignore
import 'echarts/lib/component/tooltip'
// @ts-ignore
import 'echarts/lib/component/title'
// @ts-ignore
import 'echarts/lib/component/grid'
// @ts-ignore
import 'echarts/lib/component/legend'
// @ts-ignore
import 'echarts/lib/component/graphic'

interface StatusOption {
  value: number
  label: string
}

interface RankingDataItem {
  name: string
  value: number
  percentage: number
}

@Component({
  name: 'LeakRankingIndex'
})
export default class extends Vue {
  private charts: { [key: string]: any } = {}
  private activeTab = 'repairSize'

  // 排名数据
  private repairSizeData: RankingDataItem[] = []
  private checkSizeData: RankingDataItem[] = []
  private repairMaterialData: RankingDataItem[] = []
  private checkDeptData: RankingDataItem[] = []
  private repairDeptData: RankingDataItem[] = []
  private checkUserData: RankingDataItem[] = []
  private repairUserData: RankingDataItem[] = []

  // 选项数据
  private projectOptions: IProject[] = []
  private deptList: IAllDeptInfo[] = []
  private userList: IAllUserInfo[] = []

  // 过滤参数
  private filterParams = {
    year: new Date().getFullYear(),
    startTime: '',
    endTime: '',
    projectIds: [] as number[],
    leakStatuses: [] as number[]
  }

  // 日期范围
  private dateRange: [string, string] | null = null

  // 日期选择器配置
  private pickerOptions = {
    disabledDate: (time: Date) => {
      // 限制日期范围在选定年份内
      const year = this.filterParams.year
      const start = new Date(year, 0, 1)
      const end = new Date(year, 11, 31)
      return time.getTime() < start.getTime() || time.getTime() > end.getTime()
    }
  }

  get isMobile() {
    return IsMobile()
  }

  // 状态选项
  get statusOptions(): StatusOption[] {
    return [
      { value: LeakStatus.Unreported, label: '未上报' },
      { value: LeakStatus.NeedAssignRepairDept, label: '待分配' },
      { value: LeakStatus.NeedAssignRepairUser, label: '待认领' },
      { value: LeakStatus.Repairing, label: '维修中' },
      { value: LeakStatus.Repaired, label: '完成维修' },
      { value: LeakStatus.NoNeedRepair, label: '无需维修' },
      { value: LeakStatus.CantRepair, label: '未能维修' }
    ]
  }

  // 年份选项
  get yearOptions(): number[] {
    const currentYear = new Date().getFullYear()
    const years = []
    // 显示当前年份和前4年的数据
    for (let i = 0; i < 5; i++) {
      years.push(currentYear - i)
    }
    return years
  }

  created() {
    // 初始化日期范围为当前年份的1月1日到当前日期
    this.initDateRange()
    window.addEventListener('resize', this.handleResize)
  }

  mounted() {
    this.fetchProjects()
    this.fetchDepts()
    this.fetchUsers()
    this.fetchRankingData()
    this.$nextTick(() => {
      this.initChart('repairSize')
    })
  }

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    // 销毁所有图表实例
    Object.keys(this.charts).forEach(key => {
      if (this.charts[key]) {
        this.charts[key].dispose()
      }
    })
  }

  // 初始化日期范围
  private initDateRange() {
    const year = this.filterParams.year
    const startDate = `${year}-01-01`
    const today = new Date()
    let endDate

    if (today.getFullYear() === year) {
      // 如果是当前年份，结束日期为今天
      const month = String(today.getMonth() + 1).padStart(2, '0')
      const day = String(today.getDate()).padStart(2, '0')
      endDate = `${year}-${month}-${day}`
    } else {
      // 否则是该年的最后一天
      endDate = `${year}-12-31`
    }

    this.dateRange = [startDate, endDate]
    this.filterParams.startTime = startDate
    this.filterParams.endTime = endDate
  }

  // 处理窗口大小变化
  private handleResize() {
    // 重绘当前活动的图表
    this.$nextTick(() => {
      if (this.charts[this.activeTab]) {
        this.charts[this.activeTab].resize()
      }
    })
  }

  // 处理年份变化
  private handleYearChange() {
    this.initDateRange()
    this.fetchRankingData()
  }

  // 处理日期范围变化
  private handleDateRangeChange(val: [string, string] | null) {
    if (val) {
      this.filterParams.startTime = val[0]
      this.filterParams.endTime = val[1]
    } else {
      this.initDateRange()
    }
    this.fetchRankingData()
  }

  // 处理标签页点击
  private handleTabClick() {
    this.$nextTick(() => {
      this.initChart(this.activeTab)
    })
  }

  // 处理搜索
  private handleSearch() {
    this.fetchRankingData()
  }

  // 重置过滤条件
  private resetFilter() {
    this.filterParams = {
      year: new Date().getFullYear(),
      startTime: '',
      endTime: '',
      projectIds: [],
      leakStatuses: []
    }
    this.initDateRange()
    this.fetchRankingData()
  }

  // 获取项目数据
  private async fetchProjects() {
    try {
      const { data } = await ListProjects()
      this.projectOptions = data
    } catch (error) {
      console.error('获取项目列表失败:', error)
    }
  }

  // 获取部门数据
  private async fetchDepts() {
    try {
      const { data } = await ListDepts()
      this.deptList = data
    } catch (error) {
      console.error('获取部门列表失败:', error)
    }
  }

  // 获取用户数据
  private async fetchUsers() {
    try {
      const { data } = await ListOrgUsers()
      this.userList = data
    } catch (error) {
      console.error('获取用户列表失败:', error)
    }
  }

  // 获取排名数据
  private async fetchRankingData() {
    try {
      const commonParams = {
        projectIds: this.filterParams.projectIds.length > 0 ? this.filterParams.projectIds : undefined,
        leakStatuses: this.filterParams.leakStatuses.length > 0 ? this.filterParams.leakStatuses : undefined,
        startTime: this.filterParams.startTime ? `${this.filterParams.startTime}T00:00:00Z` : undefined,
        endTime: this.filterParams.endTime ? `${this.filterParams.endTime}T23:59:59Z` : undefined
      }

      try {
        // 直接使用单个API调用获取所有排名数据
        const response = await GetRanking(commonParams)
        console.log('获取到排名数据:', response)

        // 检查响应格式
        if (response && response.data) {
          const data = response.data
          console.log('排名数据详情:', data)

          // 重要：解决字段名称不匹配问题（API返回下划线字段，但代码可能使用驼峰命名）
          // 记录原始数据，便于调试
          console.log('repair_size_ranking 原始数据:', JSON.stringify(data.repair_size_ranking))

          // 使用适配器处理数据，并进行深拷贝以解决Vue响应式问题
          this.repairSizeData = this.adaptRankingData(
            JSON.parse(JSON.stringify(data.repairSizeRanking || [])),
            'size', 'count'
          )
          this.checkSizeData = this.adaptRankingData(
            JSON.parse(JSON.stringify(data.checkSizeRanking || [])),
            'size', 'count'
          )
          this.repairMaterialData = this.adaptRankingData(
            JSON.parse(JSON.stringify(data.repairMaterialRanking || [])),
            'material', 'count'
          )
          this.checkDeptData = this.adaptRankingData(
            JSON.parse(JSON.stringify(data.checkDeptRanking || [])),
            'dept', 'count'
          )
          this.repairDeptData = this.adaptRankingData(
            JSON.parse(JSON.stringify(data.repairDeptRanking || [])),
            'dept', 'count'
          )
          this.checkUserData = this.adaptRankingData(
            JSON.parse(JSON.stringify(data.checkUserRanking || [])),
            'user', 'count'
          )
          this.repairUserData = this.adaptRankingData(
            JSON.parse(JSON.stringify(data.repairUserRanking || [])),
            'user', 'count'
          )

          // 输出转换后的数据，检查是否成功处理
          console.log('repairSizeData 处理后:', JSON.stringify(this.repairSizeData))
        } else {
          console.error('API返回格式不正确:', response)
          throw new Error('API返回格式不正确')
        }
      } catch (error) {
        console.error('新版API调用失败，使用旧版API: ', error)

        // 使用原有的单独API调用方式作为备选方案
        // 获取维修管径排名
        const repairSizeRes = await GetRanking({
          ...commonParams,
          repairSize: true
        })
        this.repairSizeData = this.formatRankingData(repairSizeRes.data || [])

        // 获取检测管径排名
        const checkSizeRes = await GetRanking({
          ...commonParams,
          checkSize: true
        })
        this.checkSizeData = this.formatRankingData(checkSizeRes.data || [])

        // 获取维修管材排名
        const repairMaterialRes = await GetRanking({
          ...commonParams,
          repairMaterial: true
        })
        this.repairMaterialData = this.formatRankingData(repairMaterialRes.data || [])

        // 获取检测部门排名
        const checkDeptRes = await GetRanking({
          ...commonParams,
          checkDept: true
        })
        this.checkDeptData = this.formatRankingData(checkDeptRes.data || [])

        // 获取维修部门排名
        const repairDeptRes = await GetRanking({
          ...commonParams,
          repairDept: true
        })
        this.repairDeptData = this.formatRankingData(repairDeptRes.data || [])

        // 获取检测人员排名
        const checkUserRes = await GetRanking({
          ...commonParams,
          checkUser: true
        })
        this.checkUserData = this.formatRankingData(checkUserRes.data || [])

        // 获取维修人员排名
        const repairUserRes = await GetRanking({
          ...commonParams,
          repairUser: true
        })
        this.repairUserData = this.formatRankingData(repairUserRes.data || [])
      }

      // 初始化当前显示的图表
      this.$nextTick(() => {
        this.initChart(this.activeTab)
      })
    } catch (error) {
      console.error('获取排名数据失败:', error)
      this.$message.error('获取排名数据失败')
    }
  }

  // 适配新的排名数据格式
  private adaptRankingData(data: any[], nameField: string, valueField: string): RankingDataItem[] {
    if (!data || data.length === 0) {
      console.log(`适配器收到空数据，字段:${nameField},${valueField}`)
      return []
    }

    console.log(`适配器原始数据(${nameField}):`, JSON.stringify(data))

    // 转换数据格式
    const formattedData = data.map(item => {
      // 确保有这些字段，并转换为正确的类型
      let name = '未知'

      // 特殊处理user和dept字段，获取它们的name属性
      if (nameField === 'user' || nameField === 'dept') {
        if (item[nameField] && typeof item[nameField] === 'object') {
          name = item[nameField].name || item[nameField].username || '未知'
        } else {
          name = item[nameField] !== undefined ? String(item[nameField]) : '未知'
        }
      } else {
        name = item[nameField] !== undefined ? String(item[nameField]) : '未知'
      }

      const value = item[valueField] !== undefined ? Number(item[valueField]) : 0
      return { name, value }
    })

    console.log(`适配器转换后数据(${nameField}):`, JSON.stringify(formattedData))

    // 计算总数
    const total = formattedData.reduce((sum, item) => sum + item.value, 0)

    // 添加百分比
    const result = formattedData.map(item => ({
      name: item.name,
      value: item.value,
      percentage: total > 0 ? item.value / total : 0
    }))

    console.log(`适配器最终数据(${nameField})(带百分比):`, JSON.stringify(result))
    return result
  }

  // 格式化排名数据，计算百分比
  private formatRankingData(data: any[]): RankingDataItem[] {
    if (!data || data.length === 0) return []

    const total = data.reduce((sum, item) => sum + item.value, 0)
    return data.map(item => ({
      name: item.name,
      value: item.value,
      percentage: total > 0 ? item.value / total : 0
    }))
  }

  // 初始化图表
  private initChart(chartType: string) {
    if (this.charts[chartType]) {
      this.charts[chartType].dispose()
    }

    const chartDom = document.getElementById(`${chartType}Chart`)
    if (!chartDom) {
      console.error(`未找到图表DOM元素: ${chartType}Chart`)
      return
    }

    const chart = echarts.init(chartDom)
    this.charts[chartType] = chart

    let data: RankingDataItem[] = []
    let title = ''

    switch (chartType) {
      case 'repairSize':
        data = this.repairSizeData
        title = '维修管径排名'
        break
      case 'checkSize':
        data = this.checkSizeData
        title = '检测管径排名'
        break
      case 'repairMaterial':
        data = this.repairMaterialData
        title = '维修管材排名'
        break
      case 'checkDept':
        data = this.checkDeptData
        title = '检测部门排名'
        break
      case 'repairDept':
        data = this.repairDeptData
        title = '维修部门排名'
        break
      case 'checkUser':
        data = this.checkUserData
        title = '检测人员排名'
        break
      case 'repairUser':
        data = this.repairUserData
        title = '维修人员排名'
        break
    }

    // 输出原始数据（用于调试）
    console.log(`初始化${title}图表，原始数据:`, JSON.stringify(data))

    // 确保数据是数组
    if (!Array.isArray(data)) {
      console.error(`${title}数据不是数组:`, data)
      data = []
    }

    // 按值从大到小排序数据
    const sortedData = [...data].sort((a, b) => b.value - a.value).slice(0, 10)
    console.log(`${title}排序后数据:`, JSON.stringify(sortedData))

    // 判断是否有数据
    const hasData = sortedData && sortedData.length > 0
    console.log(`${title} 是否有数据:`, hasData, `数据长度:`, sortedData.length)

    // 定义ECharts选项
    const option: any = {
      title: {
        text: title,
        left: 'center'
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: (params: any) => {
          if (!params || !params[0]) return ''
          const dataItem = params[0]
          const percentage = (dataItem.data.percentage * 100).toFixed(2)
          return `${dataItem.name}: ${dataItem.value} (${percentage}%)`
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'value',
        boundaryGap: [0, 0.01],
        show: hasData
      },
      yAxis: {
        type: 'category',
        data: hasData ? sortedData.map(item => item.name) : [],
        axisLabel: {
          width: 80,
          overflow: 'truncate'
        },
        show: hasData
      },
      series: [
        {
          name: '数量',
          type: 'bar',
          data: hasData ? sortedData.map(item => ({ value: item.value, percentage: item.percentage })) : [],
          // 配置渐变色
          itemStyle: {
            color: function(params: any) {
              // 单调颜色，根据索引生成不同深浅
              const colorList = [
                '#83bff6', '#188df0', '#1666dd', '#1050c0', '#0a3ca3',
                '#072f86', '#05226a', '#03164d', '#020d31', '#010514'
              ]
              return colorList[params.dataIndex % colorList.length]
            }
          },
          emphasis: {
            itemStyle: {
              color: '#2378f7'
            }
          }
        }
      ]
    }

    // 添加无数据提示
    if (!hasData) {
      option.graphic = [
        {
          type: 'text',
          left: 'center',
          top: 'middle',
          style: {
            text: '暂无数据',
            fontSize: 20,
            fill: '#999'
          }
        }
      ]
    }

    try {
      console.log(`设置${title}图表选项:`, JSON.stringify(option))
      chart.setOption(option)
      chart.resize()
      console.log(`${title}图表初始化完成`)
    } catch (error) {
      console.error(`初始化${title}图表失败:`, error)
    }
  }
}
